import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const CategoryList = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteModal, setDeleteModal] = useState({ show: false, category: null });

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL + 'list-categories.php';
    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          showToast('Failed to load categories', 'error');
        }
      })
      .catch(() => showToast('Error fetching categories', 'error'));
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.categoryname.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (category) => {
    navigate(`/edit-category/${category.id}`, { state: { category } });
    showToast(`Editing category: ${category.categoryname}`, 'info');
  };

  const handleDelete = (category) => {
    setDeleteModal({ show: true, category });
  };

  const confirmDelete = async () => {
    const { category } = deleteModal;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL + 'delete-category.php';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: category.id
        })
      });

      const data = await response.json();
      
      if (data.status) {
        setCategories(categories.filter(cat => cat.id !== category.id));
        showToast(`Category "${category.categoryname}" deleted successfully`, 'success');
      } else {
        showToast(data.message || 'Failed to delete category', 'error');
      }
    } catch (error) {
      showToast('Error deleting category', 'error');
    }
    
    setDeleteModal({ show: false, category: null });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, category: null });
  };

  const handleAddCategory = () => {
    navigate('/add-category');
    showToast('Navigating to add new category', 'info');
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Category Management</h1>
          <p>Manage menu categories</p>
        </div>
        <button className="add-unit-button" onClick={handleAddCategory}>
          + Add New Category
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search categories by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Category Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map(category => (
              <tr key={category.id}>
                <td>
                  <div className="unit-info">
                    <div className="unit-icon">
                      🏷️
                    </div>
                    <span>{category.categoryname}</span>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(category)}
                    >
                      Edit
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(category)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredCategories.length} of {categories.length} categories
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete Category</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the category <strong>"{deleteModal.category?.categoryname}"</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                No, Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList; 